import { LoadingButton } from "@mui/lab";
import { DatePicker } from '@mui/x-date-pickers';

import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { apiRequest } from "api-client";
import apiEndpoints from "constants/endpoints";
import moment from "moment/moment";
import { useEffect, useState } from "react";
import { jsonToCSV } from 'react-papaparse';
import { downloadCSV } from "utils/file";
import { formatEventDate } from "utils/time";


export const AudienceReports = () => {
  const [start, setStart] = useState(moment().subtract(14, 'days').toDate());
  const [end, setEnd] = useState(new Date());
  const [reports, setReports] = useState([]);
  const [sortBy, setSortBy] = useState('user');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const getReportParams = (includeRaw = false) => {
    const params = {
      start: moment(start).toISOString(),
      end: moment(end).toISOString(),
    };
    if (includeRaw) {
      params.isRaw = 1;
    }
    return params;
  };

  useEffect(() => {
    if (start && end) {
      setIsLoading(true);
      apiRequest.get(`/${apiEndpoints.audienceReports}`, { params: getReportParams() }).then(data => {
        setReports(data.map(item => ({
          Id: item.eventId,
          Event: item.eventName,
          Time: `${formatEventDate(item.eventStartTime)} - ${formatEventDate(item.eventEndTime)}`,
          Session: item.sessionCount,
          User: item.userCount,
          "Total Duration (hours)": (item.totalDuration / 3600).toFixed(2)
        })));
        setIsLoading(false);
      }).catch(err => {
        setIsLoading(false);
        console.warn(err);
      });
    }
  }, [start, end]);

  const downloadRawData = () => {
    setIsDownloading(true);
    apiRequest.get(`/${apiEndpoints.audienceReports}`, { params: getReportParams(true) }).then(data => {
      downloadCSV(jsonToCSV(data), 'raw-data.csv');
      setIsDownloading(false);
    }).catch(err => {
      setIsDownloading(false);
      console.warn(err);
    });
  }

  const exportCSV = () => {
    downloadCSV(jsonToCSV(reports), 'report.csv');
  }

  const sortReports = (sort) => {
    setSortBy(sort);
    setReports(reports.sort((a,b) => {
      if (sort === 'session') {
        return b.Session - a.Session;
      }
      if (sort === 'user') {
        return b.User - a.User;
      }
      if (sort === 'duration') {
        return b['Total Duration (hours)'] - a['Total Duration (hours)'];
      }
      return 0;
    }));
  }

  return (
    <Paper>
      <Box p={2}>
        <Typography variant="h6" mb={2}>
          Audience Reports
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={5} sm={4} md={2}>
            <DatePicker
              fullWidth
              clearable={true}
              value={moment(start)}
              onChange={(value) => setStart(value ? moment(value).toDate() : null)}
              label="Start"
              slotProps={{ textField: { size: 'small' } }}
            />
          </Grid>
          <Grid item xs={5} sm={4} md={2}>
            <DatePicker
              fullWidth
              clearable={true}
              value={moment(end)}
              onChange={(value) => setEnd(value ? moment(value).toDate() : null)}
              label="End"
              slotProps={{ textField: { size: 'small' } }}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportCSV}
              className="mr-1"
            >Export CSV</Button>
            <LoadingButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              loading={isDownloading}
              onClick={downloadRawData}>Export Raw Data</LoadingButton>
          </Grid>
        </Grid>
        {!isLoading &&
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={400}>Event</TableCell>
                <TableCell>Event Time</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'session'}
                    direction={'desc'}
                    onClick={() => sortReports('session')}
                  >
                    Session
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'user'}
                    direction={'desc'}
                    onClick={() => sortReports('user')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'duration'}
                    direction={'desc'}
                    onClick={() => sortReports('duration')}
                  >
                    Total Duration (hours)
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report, index) =>
                <TableRow key={index}>
                  <TableCell>{report.Event}</TableCell>
                  <TableCell>{report.Time}</TableCell>
                  <TableCell>{report.Session}</TableCell>
                  <TableCell>{report.User}</TableCell>
                  <TableCell>{report['Total Duration (hours)']}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        }
        {isLoading &&
          <Box mt={4} textAlign="center">
            <CircularProgress />
          </Box>
        }
      </Box>
    </Paper>
  );
}